import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Package, Truck, Globe, CheckCircle, Shield, Award } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import QuoteModal from '../components/modals/QuoteModal';
import { useTranslation } from '../hooks/useTranslation';

const ImportExportPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);

  const materials = [
    {
      name: 'Structural Steel',
      description: 'High-grade steel beams, columns, and reinforcement bars from certified European mills',
      countries: ['Germany', 'Austria', 'Sweden'],
      certification: 'CE, ISO 9001, EN 10025'
    },
    {
      name: 'Architectural Glass',
      description: 'Premium glass panels, facades, and energy-efficient glazing systems',
      countries: ['Belgium', 'Netherlands', 'France'], 
      certification: 'CE, EN 572, ISO 12543'
    },
    {
      name: 'Concrete Products',
      description: 'Precast concrete, blocks, and specialized concrete mixtures',
      countries: ['Netherlands', 'Germany', 'Denmark'],
      certification: 'CE, EN 206, ISO 17025'
    },
    {
      name: 'Insulation Systems',
      description: 'Thermal and acoustic insulation materials for sustainable construction',
      countries: ['Austria', 'Switzerland', 'Finland'],
      certification: 'CE, EN 13163, ISO 16000'
    }
  ];

  const benefits = [
    {
      icon: Shield,
      title: 'Quality Assurance',
      description: 'All materials certified to European standards with full traceability and quality documentation.'
    },
    {
      icon: Globe,
      title: 'EU-Wide Network',
      description: 'Direct partnerships with over 150+ certified manufacturers across 25 European countries.'
    },
    {
      icon: Truck,
      title: 'Streamlined Logistics',
      description: 'Complete supply chain management from factory to your construction site.'
    },
    {
      icon: Award,
      title: 'Competitive Pricing',
      description: 'Bulk purchasing power and direct relationships ensure best market prices.'
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Requirements Analysis',
      description: 'We analyze your project specifications, quantities, and delivery requirements.'
    },
    {
      step: '02', 
      title: 'Supplier Matching',
      description: 'Our team identifies certified suppliers that meet your exact material specifications.'
    },
    {
      step: '03',
      title: 'Quality Verification',
      description: 'Materials undergo rigorous quality checks and certification verification.'
    },
    {
      step: '04',
      title: 'Logistics Coordination',
      description: 'We handle customs, documentation, and delivery coordination to your site.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-gray-900 text-white py-4">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-white hover:bg-gray-800 mb-4"
          >
            <ArrowLeft size={20} className="mr-2" />
            {t('pages.importExport.backToHome')}
          </Button>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-blue-100 text-blue-800">{t('pages.importExport.badge')}</Badge>
              <h1 className="text-4xl md:text-5xl font-light text-gray-900 mb-6">
                {t('pages.importExport.title')}
                <span className="block font-medium">{t('pages.importExport.titleFrom')}</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                {t('pages.importExport.description')}
              </p>
              <div className="flex space-x-4">
                <Button 
                  onClick={() => setIsQuoteModalOpen(true)}
                  size="lg" 
                  className="bg-gray-900 hover:bg-gray-800"
                >
                  <Package size={20} className="mr-2" />
                  {t('pages.importExport.requestMaterialsQuote')}
                </Button>
                <Button size="lg" variant="outline">
                  {t('pages.importExport.viewCatalog')}
                </Button>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?q=80&w=2070" 
                alt="Construction Materials"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Materials Categories */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl font-light text-gray-900 text-center mb-12">
            {t('pages.importExport.materialCategories')}
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {materials.map((material, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <h3 className="text-xl font-medium text-gray-900">{material.name}</h3>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">{material.description}</p>
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Available from:</p>
                    <div className="flex flex-wrap gap-2">
                      {material.countries.map((country) => (
                        <Badge key={country} variant="outline">{country}</Badge>
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    <strong>Certifications:</strong> {material.certification}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl font-light text-gray-900 text-center mb-12">
            {t('pages.importExport.whyChoose')}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-gray-900 rounded-lg mx-auto mb-4 flex items-center justify-center">
                    <IconComponent size={32} className="text-white" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl font-light text-gray-900 text-center mb-12">
            {t('pages.importExport.ourImportProcess')}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="relative">
                <div className="text-4xl font-light text-gray-300 mb-4">{step.step}</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 -right-4 w-8 h-px bg-gray-300"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-light mb-6">
            {t('pages.importExport.readyToSource')}
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            {t('pages.importExport.contactExperts')}
          </p>
          <Button 
            onClick={() => setIsQuoteModalOpen(true)}
            size="lg" 
            className="bg-white text-gray-900 hover:bg-gray-100"
          >
            {t('pages.importExport.getStartedToday')}
          </Button>
        </div>
      </section>

      <QuoteModal 
        isOpen={isQuoteModalOpen} 
        onClose={() => setIsQuoteModalOpen(false)} 
      />
    </div>
  );
};

export default ImportExportPage;